/**
 * Per-user limits on top of the role (set by the owner or a manager): which devices someone
 * can see, and the hours they may switch equipment. FMS staff have no limits.
 */
import type { User } from "./model.ts";

export function canSeeDevice(user: Pick<User, "role" | "devices">, devEui: string): boolean {
  return user.role === "fms-admin" || !user.devices || user.devices.includes(devEui);
}

/**
 * A person limited to whole sites (farms) sees every device at those sites, including ones
 * added after the limit was set. This turns that into the plain device list `canSeeDevice`
 * checks, from the account's devices as they are now; call it wherever a user is loaded
 * to be checked against devices (sign-in, notifications).
 */
export function withSiteAccess<U extends Pick<User, "devices" | "sites">>(user: U, devices: ReadonlyArray<{ devEui: string; siteId: string }>): U {
  if (!user.sites) return user;
  const sites = new Set(user.sites);
  const list = new Set(user.devices ?? []);
  for (const d of devices) if (sites.has(d.siteId)) list.add(d.devEui);
  return { ...user, devices: [...list] };
}

const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;
export const isTime = (s: string) => HHMM.test(s);

/** Local "HH:MM" at a time zone (the device's site), e.g. "21:05". */
export function localTime(now: number, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date(now));
  const h = parts.find((p) => p.type === "hour")?.value ?? "00";
  const m = parts.find((p) => p.type === "minute")?.value ?? "00";
  return `${h}:${m}`;
}

/** True when the user may switch equipment right now at that site. */
export function withinControlHours(user: Pick<User, "role" | "controlHours">, now: number, timeZone: string): boolean {
  if (user.role === "fms-admin" || !user.controlHours) return true;
  const { from, to } = user.controlHours;
  const t = localTime(now, timeZone);
  // "21:00" to "05:00" means overnight.
  return from <= to ? t >= from && t < to : t >= from || t < to;
}

/** "5:00 am to 9:00 pm" */
export function hoursText(h: { from: string; to: string }): string {
  const f = (s: string) => {
    const [hh, mm] = s.split(":").map(Number) as [number, number];
    return `${hh % 12 || 12}:${String(mm).padStart(2, "0")} ${hh < 12 ? "am" : "pm"}`;
  };
  return `${f(h.from)} to ${f(h.to)}`;
}
